/**
 * Extract metadata from a URL
 * This is a client-side implementation that fetches the page and parses meta tags
 */

export interface Metadata {
  title: string;
  description: string | null;
  favicon: string | null;
  thumbnail: string | null;
  domain: string;
}

/**
 * Extract domain from URL
 */
export function extractDomain(url: string): string {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.replace('www.', '');
  } catch {
    return '';
  }
}

/**
 * Get favicon URL from domain
 */
export function getFaviconUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    return `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=64`;
  } catch {
    return '';
  }
}

/**
 * Fetch and extract metadata from URL
 * Note: This may fail due to CORS. In production, use a server-side proxy.
 */
export async function extractMetadata(url: string): Promise<Metadata> {
  const domain = extractDomain(url);
  const favicon = getFaviconUrl(url);

  // Default metadata
  const metadata: Metadata = {
    title: url,
    description: null,
    favicon,
    thumbnail: null,
    domain,
  };

  try {
    // Try to fetch the page (may fail due to CORS)
    const response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
    });

    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Extract title
    const titleTag = doc.querySelector('title');
    const ogTitle = doc.querySelector('meta[property="og:title"]');
    metadata.title = ogTitle?.getAttribute('content') || titleTag?.textContent || url;

    // Extract description
    const metaDescription = doc.querySelector('meta[name="description"]');
    const ogDescription = doc.querySelector('meta[property="og:description"]');
    metadata.description =
      ogDescription?.getAttribute('content') || metaDescription?.getAttribute('content') || null;

    // Extract thumbnail
    const ogImage = doc.querySelector('meta[property="og:image"]');
    metadata.thumbnail = ogImage?.getAttribute('content') || null;
  } catch (error) {
    console.warn('Failed to extract metadata (likely CORS issue):', error);
    // Use URL as fallback title
    metadata.title = url;
  }

  return metadata;
}
